/* ── Gateway environments ──────────────────────────────────────────────────
 *
 * Deployment targets, and what is deployed to them. The Apigee shape: a product
 * version is the artefact, an environment is where it runs, and deploying binds
 * one to the other.
 *
 * WHY A SEPARATE FILE. Frontend updates arrive as `static.zip` and are merged
 * three-way; index.html conflicts every time. A file the export has never heard
 * of cannot conflict, so the merge surface is five one-line hooks, all listed in
 * `docs/ui-deltas/gateway-environments.md`.
 *
 * All top-level names here are unique across the static bundle — the scripts
 * share one global scope, so a repeated name silently shadows one definition.
 *
 * Depends on: API (index.html), React globals.
 */

const GW_ENV_MASK='********';

/** Env-level state, at a glance. Order matters: the first true one is the headline. */
function gwEnvBadges(env){
  const out=[];
  // "protected", not "production": the badge means destructive actions are guarded,
  // and labelling it with the word most environments are already called renders as
  // "production production" next to the slug.
  if(env.production)        out.push(['error','protected']);
  if(env.defaultEnvironment)out.push(['info','default']);
  if(!env.enabled)          out.push(['warning','frozen']);
  else if(!env.usable)      out.push(['warning','incomplete']);
  return out;
}

function GatewayEnvBadges({env}){
  return(
    <React.Fragment>
      {gwEnvBadges(env).map(([tone,label])=>(
        <span key={label} className={'badge badge-'+tone} style={{fontSize:10,flex:'none'}}>{label}</span>
      ))}
    </React.Fragment>
  );
}

/** Sync/teardown output, rendered the way the Access tab renders it. */
function GatewayEnvSyncReport({result}){
  if(!result)return null;
  return(
    <div style={{marginTop:12,fontSize:12,maxHeight:220,overflowY:'auto'}}>
      {(result.applied||[]).map((a,i)=><div key={'a'+i} style={{color:'var(--success,#10b981)'}}>✓ {a}</div>)}
      {(result.skipped||[]).map((s,i)=><div key={'s'+i} style={{color:'var(--text3)'}}>– {s}</div>)}
      {(result.errors ||[]).map((e,i)=><div key={'e'+i} style={{color:'var(--error,#ef4444)'}}>✕ {e}</div>)}
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */

function GatewayEnvironmentsPage({showToast,confirm}){
  const [envs,setEnvs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null);
  const [editing,setEditing]=useState(null);   // env object, or {} for a new one
  const [busy,setBusy]=useState('');

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const list=await API('GET','/api/admin/gateway-environments');
      setEnvs(Array.isArray(list)?list:[]);
    }catch(e){showToast&&showToast('Could not load environments: '+e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);

  useEffect(()=>{reload();},[reload]);

  const save=async(form,isNew)=>{
    setBusy('save');
    try{
      if(isNew) await API('POST','/api/admin/gateway-environments',form);
      else      await API('PUT','/api/admin/gateway-environments/'+form.slug,form);
      showToast&&showToast(isNew?'Environment created':'Environment saved','success');
      setEditing(null);
      await reload();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const makeDefault=async(env)=>{
    setBusy('default:'+env.slug);
    try{
      await API('POST','/api/admin/gateway-environments/'+env.slug+'/default');
      showToast&&showToast(env.slug+' is now the default target','success');
      await reload();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const remove=async(env)=>{
    const ok=confirm
      ?await confirm('Delete the "'+env.slug+'" environment?\n\nAxon stops managing that '
        +'data plane. Any routes already on it keep serving until someone removes them there.')
      :window.confirm('Delete '+env.slug+'?');
    if(!ok)return;
    setBusy('del:'+env.slug);
    try{
      await API('DELETE','/api/admin/gateway-environments/'+env.slug);
      showToast&&showToast('Environment deleted','success');
      if(selected===env.slug)setSelected(null);
      await reload();
    }catch(e){
      // The server refuses while products still name it, and lists them — that
      // message is more useful than a generic failure toast.
      showToast&&showToast(e.message,'error');
    }
    finally{setBusy('');}
  };

  const current=envs.find(e=>e.slug===selected)||null;

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Gateway Environments</h1>
          <div className="page-subtitle">
            Deployment targets. A product version is deployed <em>to</em> an environment —
            each one is its own APISIX data plane with its own routes and upstreams.
          </div>
        </div>
        <button className="btn btn-primary" style={typeof TAXLAB_CTA_STYLE!=='undefined'?TAXLAB_CTA_STYLE:undefined}
                onClick={()=>setEditing({})}>
          New Environment
        </button>
      </div>

      {loading&&<div className="card"><div className="card-body">Loading…</div></div>}

      {!loading&&envs.length===0&&(
        <div className="card"><div className="card-body" style={{textAlign:'center',padding:48}}>
          <h3 style={{margin:'0 0 8px'}}>No environments defined</h3>
          <p style={{color:'var(--text3)',margin:0}}>
            Until one exists Axon falls back to the single gateway in
            <code> connector-runtime.properties</code>. Defining one takes over from it.
          </p>
          <button className="btn btn-primary" style={{marginTop:16}} onClick={()=>setEditing({})}>
            Create the first environment
          </button>
        </div></div>
      )}

      {!loading&&envs.length>0&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:16,marginBottom:20}}>
          {envs.map(env=>(
            <div key={env.slug} className="card"
                 onClick={()=>setSelected(selected===env.slug?null:env.slug)}
                 style={{cursor:'pointer',
                         borderColor:selected===env.slug
                           ?(env.production?'var(--error,#ef4444)':'var(--brand-start)')
                           :undefined,
                         opacity:env.enabled?1:0.65}}>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:15,fontFamily:'monospace'}}>{env.slug}</div>
                    <div style={{fontSize:12,color:'var(--text3)',marginTop:2}}>{env.name}</div>
                  </div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end'}}>
                    <GatewayEnvBadges env={env}/>
                  </div>
                </div>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:12,lineHeight:1.7}}>
                  <div style={{fontFamily:'monospace',wordBreak:'break-all'}}>{env.adminUrl||'— no admin URL —'}</div>
                  <div style={{fontFamily:'monospace',wordBreak:'break-all'}}>
                    ↳ {env.taxlabUpstreamHost||'— no upstream host —'}
                  </div>
                  <div style={{marginTop:6}}>
                    {env.deployedProductCount} product{env.deployedProductCount===1?'':'s'} deployed
                    {env.adminApiKey?'':' · no admin key set'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {current&&(
        <GatewayEnvDetail env={current}
                          onEdit={()=>setEditing(current)}
                          onMakeDefault={()=>makeDefault(current)}
                          onDelete={()=>remove(current)}
                          onClose={()=>setSelected(null)}
                          onChanged={reload}
                          busy={busy}
                          showToast={showToast}
                          confirm={confirm}/>
      )}

      {editing&&(
        <GatewayEnvModal env={editing}
                         isNew={!editing.slug}
                         saving={busy==='save'}
                         onCancel={()=>setEditing(null)}
                         onSave={form=>save(form,!editing.slug)}/>
      )}
    </div>
  );
}

/* ── Detail: connectivity and deployments ──────────────────────────────── */

function GatewayEnvDetail({env,onEdit,onMakeDefault,onDelete,onClose,onChanged,busy,showToast,confirm}){
  const [health,setHealth]=useState(null);
  const [testing,setTesting]=useState(false);

  const test=async()=>{
    setTesting(true);setHealth(null);
    try{
      setHealth(await API('POST','/api/admin/gateway-environments/'+env.slug+'/test'));
    }catch(e){setHealth({healthy:false,error:e.message});}
    finally{setTesting(false);}
  };

  return(
    <div className="card">
      <div className="card-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
        <div className="card-title" style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <span style={{fontFamily:'monospace'}}>{env.slug}</span>
          <GatewayEnvBadges env={env}/>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-sm btn-secondary" onClick={test} disabled={testing}>
            {testing?'Testing…':'Test connection'}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={onEdit}>Edit</button>
          {!env.defaultEnvironment&&(
            <button className="btn btn-sm btn-secondary" onClick={onMakeDefault}
                    disabled={busy.startsWith('default')}>Make default</button>
          )}
          <button className="btn btn-sm btn-danger" onClick={onDelete}
                  disabled={busy.startsWith('del')}>Delete</button>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
      <div className="card-body">
        {!env.usable&&(
          <div className="badge badge-warning" style={{display:'block',padding:'8px 10px',marginBottom:16}}>
            {env.enabled
              ? 'Incomplete — an admin URL and admin key are both required before anything can be pushed here.'
              : 'Frozen. Syncs skip this environment; its routes keep serving whatever was last pushed.'}
          </div>
        )}

        {health&&(
          <div style={{marginBottom:16,fontSize:13}}>
            {health.healthy
              ? <span style={{color:'var(--success,#10b981)'}}>
                  ✓ Reachable — {health.routes} route{health.routes===1?'':'s'} on this data plane
                </span>
              : <span style={{color:'var(--error,#ef4444)'}}>✕ Unreachable{health.error?' — '+health.error:''}</span>}
            {health.redis&&(
              <div style={{marginTop:4}}>
                {health.redis.ok
                  ? <span style={{color:'var(--success,#10b981)'}}>✓ Redis {health.redis.address} answered in {health.redis.millis} ms</span>
                  : <span style={{color:'var(--error,#ef4444)'}}>✕ Redis {health.redis.address} — {health.redis.detail}. AI caching is off and counters fail open or closed per product until it is back.</span>}
              </div>
            )}
          </div>
        )}

        <div style={{display:'flex',gap:24,flexWrap:'wrap',fontSize:12,marginBottom:20}}>
          <div>
            <div style={{color:'var(--text3)'}}>Admin API</div>
            <div style={{fontFamily:'monospace'}}>{env.adminUrl||'—'}</div>
          </div>
          <div>
            <div style={{color:'var(--text3)'}}>Data plane</div>
            <div style={{fontFamily:'monospace'}}>{env.dataPlaneUrl||'—'}</div>
          </div>
          <div>
            <div style={{color:'var(--text3)'}}>Upstream host</div>
            <div style={{fontFamily:'monospace'}}>{env.taxlabUpstreamHost||'—'}</div>
          </div>
          <div>
            <div style={{color:'var(--text3)'}}>Axon upstream</div>
            <div style={{fontFamily:'monospace'}}>{env.axonUpstream||'—'}</div>
          </div>
          <div>
            <div style={{color:'var(--text3)'}}>Admin key</div>
            <div style={{fontFamily:'monospace'}}>{env.adminApiKey?'set':'not set'}</div>
          </div>
          <div>
            <div style={{color:'var(--text3)'}}>Redis (AI cache &amp; shared limits)</div>
            <div style={{fontFamily:'monospace'}}>
              {env.redisHost?(env.redisHost+':'+(env.redisPort||6379)+(env.redisPassword?' · auth':'')+(env.redisSsl?' · tls':''))
                            :'— counters per node, no AI cache'}
            </div>
          </div>
        </div>

        <GatewayEnvDeployments env={env} onChanged={onChanged} showToast={showToast} confirm={confirm}/>
      </div>
    </div>
  );
}

/* ── Deployments ───────────────────────────────────────────────────────── */

/**
 * What runs on this environment, and the deploy/undeploy actions.
 *
 * Deploying is one call that both records the binding and pushes the routes —
 * they are not separable, because a deployment shown in the UI that was never
 * pushed is worse than no deployment at all.
 */
function GatewayEnvDeployments({env,onChanged,showToast,confirm}){
  const [deployed,setDeployed]=useState([]);
  const [products,setProducts]=useState([]);
  const [pick,setPick]=useState('');
  const [busy,setBusy]=useState('');
  const [result,setResult]=useState(null);
  const [loading,setLoading]=useState(true);

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const [d,p]=await loadAll([
        ['Deployments',API('GET','/api/admin/gateway-environments/'+env.slug+'/deployments'),[]],
        ['API products',API('GET','/api/admin/api-products'),[]],
      ],showToast);
      setDeployed(Array.isArray(d)?d:[]);
      setProducts(Array.isArray(p)?p:[]);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  },[env.slug,showToast]);

  useEffect(()=>{reload();},[reload]);

  const deployedIds=new Set(deployed.map(d=>d.productId));
  const candidates=products.filter(p=>!deployedIds.has(p.id));

  const deploy=async()=>{
    if(!pick)return;
    setBusy('deploy');setResult(null);
    try{
      const res=await API('POST','/api/admin/gateway-environments/'+env.slug+'/deployments/'+pick);
      setResult(res.sync||null);
      showToast&&showToast('Deployed to '+env.slug,(res.sync&&res.sync.ok===false)?'error':'success');
      setPick('');
      await reload();await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const undeploy=async(row)=>{
    const warn=env.production
      ? '\n\nThis is a PRODUCTION environment. Every route for '+row.name
        +' stops serving immediately, for every subscriber.'
      : '';
    const ok=confirm
      ?await confirm('Undeploy '+row.name+' '+row.apiVersion+' from '+env.slug+'?'+warn)
      :window.confirm('Undeploy '+row.name+' from '+env.slug+'?');
    if(!ok)return;

    setBusy('undeploy:'+row.productId);setResult(null);
    try{
      // Production refuses without confirm=true server-side as well; the operator
      // already answered the dialog above, so pass it through.
      const q=env.production?'?confirm=true':'';
      const res=await API('DELETE',
        '/api/admin/gateway-environments/'+env.slug+'/deployments/'+row.productId+q);
      setResult(res.teardown||null);
      showToast&&showToast('Undeployed from '+env.slug,'success');
      await reload();await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  return(
    <div>
      <div style={{fontWeight:600,marginBottom:4}}>Deployments</div>
      <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
        Deploying pushes the product's routes, upstreams and consumer group to this data
        plane. Each environment holds an independent copy — sandbox and production can
        legitimately differ.
      </p>

      {loading&&<div style={{fontSize:12,color:'var(--text3)'}}>Loading…</div>}

      {!loading&&deployed.length===0&&(
        <div style={{fontSize:12,color:'var(--text3)',padding:'12px 0'}}>Nothing deployed here yet.</div>
      )}

      {!loading&&deployed.length>0&&(
        <div style={{overflowX:'auto',marginBottom:16}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                <th style={{padding:'8px 6px'}}>Product</th>
                <th style={{padding:'8px 6px'}}>Version</th>
                <th style={{padding:'8px 6px'}}>Base path</th>
                <th style={{padding:'8px 6px'}}>State</th>
                <th style={{padding:'8px 6px',textAlign:'right'}}></th>
              </tr>
            </thead>
            <tbody>
              {deployed.map(row=>(
                <tr key={row.productId} style={{borderTop:'1px solid var(--border)'}}>
                  <td style={{padding:'10px 6px',fontWeight:500}}>{row.name}</td>
                  <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:12}}>{row.apiVersion}</td>
                  <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:11}}>{row.basePath}</td>
                  <td style={{padding:'10px 6px'}}>
                    <span className={'badge badge-'+
                      (typeof VERSION_LIFECYCLE_BADGE!=='undefined'
                        ? (VERSION_LIFECYCLE_BADGE[row.lifecycle]||'gray') : 'gray')}
                          style={{fontSize:10}}>{row.lifecycle}</span>
                  </td>
                  <td style={{padding:'10px 6px',textAlign:'right'}}>
                    <button className="btn btn-xs btn-danger"
                            disabled={busy==='undeploy:'+row.productId}
                            onClick={()=>undeploy(row)}>Undeploy</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {candidates.length>0&&(
        <div style={{display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
          <div className="form-group" style={{margin:0,flex:'1 1 320px',maxWidth:420}}>
            <label className="form-label">Deploy a product version here</label>
            <select className="form-input" value={pick} onChange={e=>setPick(e.target.value)}>
              <option value="">— choose —</option>
              {candidates.map(p=>(
                <option key={p.id} value={p.id}>
                  {p.name}{p.apiVersion?' · '+p.apiVersion:''}{p.lifecycle?' · '+p.lifecycle:''}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={deploy}
                  disabled={!pick||busy==='deploy'||!env.usable}>
            {busy==='deploy'?'Deploying…':'Deploy'}
          </button>
          {!env.usable&&(
            <span style={{fontSize:11,color:'var(--text3)'}}>
              Environment must be enabled and complete before deploying.
            </span>
          )}
        </div>
      )}

      <GatewayEnvSyncReport result={result}/>
    </div>
  );
}

/* ── Create / edit ─────────────────────────────────────────────────────── */

function GatewayEnvModal({env,isNew,saving,onCancel,onSave}){
  const [form,setForm]=useState({
    slug:env.slug||'',name:env.name||'',description:env.description||'',
    adminUrl:env.adminUrl||'',adminApiKey:'',dataPlaneUrl:env.dataPlaneUrl||'',
    axonUpstream:env.axonUpstream||'',taxlabUpstreamHost:env.taxlabUpstreamHost||'',
    enabled:env.enabled!==false,production:!!env.production,sortOrder:env.sortOrder||0,
    redisHost:env.redisHost||'',redisPort:env.redisPort||'',redisDatabase:env.redisDatabase||'',
    redisPassword:'',redisSsl:!!env.redisSsl
  });
  const set=(k,v)=>setForm(f=>Object.assign({},f,{[k]:v}));

  const submit=()=>{
    const body=Object.assign({},form);
    // An empty key on edit means "leave it alone" — sending a blank would be read as
    // a real value and blank out a working gateway credential.
    if(!isNew&&!body.adminApiKey)delete body.adminApiKey;
    // Same rule for the Redis password; and blank numbers mean "the default", not 0.
    if(!body.redisPassword)delete body.redisPassword;
    body.redisPort=body.redisPort===''?null:Number(body.redisPort);
    body.redisDatabase=body.redisDatabase===''?null:Number(body.redisDatabase);
    onSave(body);
  };

  return(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{maxWidth:620}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{isNew?'New gateway environment':'Edit '+env.slug}</div>
        </div>
        <div className="modal-body"><div className="modal-body-scroll">
          <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div className="form-group" style={{flex:'0 0 170px'}}>
              <label className="form-label">Slug</label>
              <input className="form-input" value={form.slug} disabled={!isNew}
                     onChange={e=>set('slug',e.target.value)}
                     style={{fontFamily:'monospace'}} placeholder="production"/>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                {isNew?'Products join on this.':'Immutable — products reference it.'}
              </div>
            </div>
            <div className="form-group" style={{flex:'1 1 220px'}}>
              <label className="form-label">Name</label>
              <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)}
                     placeholder="Production"/>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description}
                   onChange={e=>set('description',e.target.value)}/>
          </div>

          <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div className="form-group" style={{flex:'1 1 300px'}}>
              <label className="form-label">Gateway Admin API</label>
              <input className="form-input" value={form.adminUrl} onChange={e=>set('adminUrl',e.target.value)}
                     style={{fontFamily:'monospace',fontSize:12}}
                     placeholder="http://10.0.2.11:9180/apisix/admin"/>
            </div>
            <div className="form-group" style={{flex:'1 1 220px'}}>
              <label className="form-label">Admin key</label>
              <input className="form-input" type="password" value={form.adminApiKey}
                     onChange={e=>set('adminApiKey',e.target.value)}
                     style={{fontFamily:'monospace',fontSize:12}}
                     placeholder={isNew?'':(env.adminApiKey?'unchanged':'not set')}/>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                Write-only. It is never returned by a read; leave blank to keep the stored one.
              </div>
            </div>
          </div>

          <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div className="form-group" style={{flex:'1 1 240px'}}>
              <label className="form-label">Data plane URL</label>
              <input className="form-input" value={form.dataPlaneUrl}
                     onChange={e=>set('dataPlaneUrl',e.target.value)}
                     style={{fontFamily:'monospace',fontSize:12}} placeholder="http://10.0.2.11:9080"/>
            </div>
            <div className="form-group" style={{flex:'1 1 240px'}}>
              <label className="form-label">Upstream host</label>
              <input className="form-input" value={form.taxlabUpstreamHost}
                     onChange={e=>set('taxlabUpstreamHost',e.target.value)}
                     style={{fontFamily:'monospace',fontSize:12}} placeholder="api.taxlab.ai"/>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                Where this environment's data routes proxy to. The one field that most
                needs to differ between sandbox and production.
              </div>
            </div>
            <div className="form-group" style={{flex:'1 1 200px'}}>
              <label className="form-label">Axon upstream</label>
              <input className="form-input" value={form.axonUpstream}
                     onChange={e=>set('axonUpstream',e.target.value)}
                     style={{fontFamily:'monospace',fontSize:12}} placeholder="127.0.0.1:8080"/>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                The Axon this data plane calls for tokens and credentials.
              </div>
            </div>
          </div>

          <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div className="form-group" style={{flex:'1 1 200px'}}>
              <label className="form-label">Redis host</label>
              <input className="form-input" value={form.redisHost}
                     onChange={e=>set('redisHost',e.target.value)}
                     style={{fontFamily:'monospace',fontSize:12}} placeholder="127.0.0.1"/>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                As this data plane reaches it. Enables the AI response cache, and moves AI quota
                and spend counters from each node's memory into Redis. Takes effect on the next sync.
              </div>
            </div>
            <div className="form-group" style={{flex:'0 0 90px'}}>
              <label className="form-label">Port</label>
              <input className="form-input" type="number" value={form.redisPort}
                     onChange={e=>set('redisPort',e.target.value)} placeholder="6379"/>
            </div>
            <div className="form-group" style={{flex:'0 0 70px'}}>
              <label className="form-label">DB</label>
              <input className="form-input" type="number" value={form.redisDatabase}
                     onChange={e=>set('redisDatabase',e.target.value)} placeholder="0"/>
            </div>
            <div className="form-group" style={{flex:'1 1 160px'}}>
              <label className="form-label">Redis password</label>
              <input className="form-input" type="password" autoComplete="off" value={form.redisPassword}
                     onChange={e=>set('redisPassword',e.target.value)}
                     placeholder={env.redisPassword?'unchanged':'none'}/>
              <label style={{display:'flex',gap:6,alignItems:'center',fontSize:11,marginTop:6}}>
                <input type="checkbox" checked={form.redisSsl} onChange={e=>set('redisSsl',e.target.checked)}/>
                TLS
              </label>
            </div>
          </div>

          <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,marginTop:8}}>
            <input type="checkbox" checked={form.enabled} style={{marginTop:3}}
                   onChange={e=>set('enabled',e.target.checked)}/>
            <span>
              Enabled
              <div style={{fontSize:11,color:'var(--text3)'}}>
                Off is a deliberate freeze: syncs skip it, and its routes keep serving
                whatever was last pushed.
              </div>
            </span>
          </label>

          <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,marginTop:10}}>
            <input type="checkbox" checked={form.production} style={{marginTop:3}}
                   onChange={e=>set('production',e.target.checked)}/>
            <span>
              This is a production environment
              <div style={{fontSize:11,color:'var(--text3)'}}>
                Undeploying from it requires an explicit confirmation — there is no undo
                for taking every route off production.
              </div>
            </span>
          </label>
        </div></div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}
                  disabled={saving||!form.slug||!form.adminUrl}>
            {saving?'Saving…':(isNew?'Create environment':'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
